/**
 * Labstep
 *
 * @module components/Metadata/Action/Create/Modal/Wizard/MetadataActionCreateModalWizardStepAddLookupFields
 * @desc Step: Add Lookup Fields - Select fields from the related category to add as lookup fields
 */

import { EntityCreateContainer } from 'labstep-web/containers/Entity/Create';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';
import { Resource } from 'labstep-web/models/resource.model';
import React from 'react';

interface MetadataActionCreateModalWizardStepAddLookupFieldsProps {
  targetTemplateId: string | undefined;
  selectedFields: Metadata[];
  onFieldToggle: (field: Metadata) => void;
  createdMetadata: Metadata | null;
  entity: any;
  onFinish: (create: (payloads: any[], options: any) => void) => void;
}

export const MetadataActionCreateModalWizardStepAddLookupFields: React.FC<
  MetadataActionCreateModalWizardStepAddLookupFieldsProps
> = ({
  targetTemplateId,
  selectedFields,
  onFieldToggle,
  createdMetadata,
  entity,
  onFinish,
}) => {
  if (!targetTemplateId) {
    return (
      <p style={{ color: '#666', fontStyle: 'italic' }}>
        No category selected.
      </p>
    );
  }

  return (
    <EntityCreateContainer
      entityName={Metadata.entityName}
      parentId={entity.metadata_thread.idAttr}
      parentName={MetadataThread.entityName}
      batch
    >
      {({ create, status }) => {
        // Call onFinish to pass the create function up to the wizard
        React.useEffect(() => {
          onFinish(create);
        }, [create, onFinish]);

        return (
          <ReadOnMountHOC
            type="entity"
            entityName={Resource.entityName}
            loading={{ loader: 'placeholder', cached: true }}
            id={targetTemplateId}
          >
            {({ entity: targetTemplate }) => {
              const availableFields =
                targetTemplate?.metadata_thread?.metadatas?.filter(
                  (relatedMetadata: Metadata) =>
                    relatedMetadata.label &&
                    relatedMetadata.type !==
                      MetadataType.entity_relation,
                ) || [];

              return (
                <div>
                  <p>
                    Select metadata fields from{' '}
                    <strong>{targetTemplate?.name}</strong>{' '}
                    {' to add'}:
                  </p>
                  <div
                    style={{
                      margin: '15px 0',
                      maxHeight: '300px',
                      overflowY: 'auto',
                    }}
                  >
                    {availableFields.map(
                      (relatedMetadata: Metadata) => (
                        <div
                          key={relatedMetadata.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '8px 0',
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={
                              selectedFields.find(
                                (f) => f.id === relatedMetadata.id,
                              ) !== undefined
                            }
                            onChange={() =>
                              onFieldToggle(relatedMetadata)
                            }
                          />
                          <span
                            style={{ flex: 1, cursor: 'pointer' }}
                            onClick={() =>
                              onFieldToggle(relatedMetadata)
                            }
                          >
                            <strong>{relatedMetadata.label}</strong>
                          </span>
                        </div>
                      ),
                    )}
                    {availableFields.length === 0 && (
                      <p
                        style={{
                          color: '#666',
                          fontStyle: 'italic',
                        }}
                      >
                        No available fields found on this template.
                      </p>
                    )}
                  </div>
                  <p
                    style={{
                      fontSize: '0.9em',
                      color: '#666',
                      marginTop: '15px',
                    }}
                  >
                    The value of these fields will be looked up from
                    the related entity when relations are created.
                  </p>
                </div>
              );
            }}
          </ReadOnMountHOC>
        );
      }}
    </EntityCreateContainer>
  );
};
